import React from "react"
import styled from "styled-components"
import LegalAndGeneralPng from "url:assets/images/legal-and-general.png"
import { desktop, mobile, tablet } from "components/_blueprint/Layout"
import { RiskIndividual, RiskReportData } from "hooks/useRiskReport"
import { Paragraph, Heading } from "components/_blueprint/Typography"

const smokerText = (smokerStatus) => (smokerStatus === "Yes" ? "Smoker" : "Non-Smoker")

export const IndividualCalculations = ({ riskReport }: { riskReport: RiskReportData }) => {
  if (riskReport.riskIndividual.length === 0)
    throw new Error("No individual data found in risk report")

  const isRiskJoint = riskReport.riskIndividual.length > 1
  const applicant1 = riskReport.riskIndividual[0]
  const applicant2 = isRiskJoint ? riskReport.riskIndividual[1] : null

  return (
    <Container>
      <Copy>
        <Heading size="h4">Individual applicant risks</Heading>
        <Paragraph weight={400}>
          The risks above are a combined view of all applicants in this protection review. The
          individual applicant risks are as follows:
        </Paragraph>
      </Copy>
      <RiskCardSection>
        <RiskCard applicant={applicant1} />
        {applicant2 && <RiskCard applicant={applicant2} />}
      </RiskCardSection>
      <PoweredBy>
        <PoweredByText>Powered with data from </PoweredByText>
        <PoweredByLogo src={LegalAndGeneralPng} />
      </PoweredBy>
    </Container>
  )
}

const RiskCard = ({ applicant }: { applicant: RiskIndividual }) => (
  <RiskCardContainer>
    <RiskCardHeader>{applicant.name}</RiskCardHeader>
    <RiskCardSummary>
      {/* Make sure  */}
      <RiskCardSummaryData>{`Age ${applicant.age}, `}</RiskCardSummaryData>{" "}
      <RiskCardSummaryData>{`${applicant.sex}, `}</RiskCardSummaryData>{" "}
      <RiskCardSummaryData>{`${smokerText(applicant.smokerStatus)}, `}</RiskCardSummaryData>{" "}
      <RiskCardSummaryData>{`Retire at ${applicant.retirementAge}`}</RiskCardSummaryData>
    </RiskCardSummary>
    <RiskCardBody>
      <RiskCardRow>
        <RiskCardRowText>Death</RiskCardRowText>
        <RiskCardRowVal>{applicant.deathRisk}%</RiskCardRowVal>
      </RiskCardRow>
      <RiskCardRow>
        <RiskCardRowText>Serious Illness</RiskCardRowText>
        <RiskCardRowVal>{applicant.illnessRisk}%</RiskCardRowVal>
      </RiskCardRow>
      <RiskCardRow>
        <RiskCardRowText>Unable to Work</RiskCardRowText>
        <RiskCardRowVal>{applicant.incomeRisk}%</RiskCardRowVal>
      </RiskCardRow>
      <RiskCardRowHighlighted>
        <RiskCardRowTextBold>Combined Risk</RiskCardRowTextBold>
        <RiskCardRowVal>{applicant.combinedRisk}%</RiskCardRowVal>
      </RiskCardRowHighlighted>
    </RiskCardBody>
  </RiskCardContainer>
)

const Container = styled.div({
  maxWidth: 1400,
  margin: "0 auto 32px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",

  [mobile]: {
    padding: "4px",
    gap: "4px",
  },
  [tablet]: {
    padding: "8px",
    gap: "8px",
  },
  [desktop]: {
    padding: "16px",
    gap: "16px",
  },
})

const Copy = styled.div({
  textAlign: "center",
  maxWidth: "600px",
  margin: "0 auto",
})

const RiskCardSection = styled.div({
  display: "flex",
  justifyContent: "center",

  [mobile]: {
    flexDirection: "column",
    alignItems: "center",
    gap: "1rem",
  },
  [tablet]: {
    flexDirection: "column",
    alignItems: "center",
    gap: "1.5rem",
  },
  [desktop]: {
    flexDirection: "row",
    gap: "2rem",
  },
})

const RiskCardContainer = styled.div({
  border: "1px solid #D1DDFC",
  borderRadius: "25px",
  padding: "2rem 1.5rem 1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "16px",

  [mobile]: {
    width: "100%",
  },
  [tablet]: {
    width: "100%",
  },
  [desktop]: {
    width: "400px",
  },
})

const RiskCardHeader = styled.p({
  fontWeight: 600,
  textAlign: "center",
  fontSize: "1.5rem",
  margin: 0,
})

const RiskCardSummary = styled.p({
  fontWeight: 400,
  textAlign: "center",
  fontSize: "0.9rem",
  color: "#3B4258",
  margin: 0,
})

const RiskCardSummaryData = styled.span({
  textWrap: "nowrap",
})

const RiskCardBody = styled.div({
  display: "flex",
  flexDirection: "column",
})

const RiskCardRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  padding: "4px 16px",
})

const RiskCardRowHighlighted = styled(RiskCardRow)({
  background: "#F8F9FC",
  padding: "16px",
  margin: "16px 0 0",
  borderRadius: "8px",
})

const RiskCardRowText = styled.p({
  fontWeight: 400,
  margin: "auto 0",

  [mobile]: {
    fontSize: "0.8rem",
  },
  [tablet]: {
    fontSize: "1rem",
  },
  [desktop]: {
    fontSize: "1rem",
  },
})

const RiskCardRowTextBold = styled(RiskCardRowText)({
  fontWeight: 500,
})

const RiskCardRowVal = styled.p({
  fontWeight: 600,
  background: "#D1DDFC",
  padding: "16px",
  borderRadius: "8px",
  margin: 0,
  textAlign: "center",

  [mobile]: {
    fontSize: "0.8rem",
    width: "70px",
  },
  [tablet]: {
    fontSize: "1rem",
    width: "80px",
  },
  [desktop]: {
    fontSize: "1rem",
    width: "80px",
  },
})

const PoweredBy = styled.div({
  maxWidth: 1400,
  textAlign: "center",
  display: "flex",
  justifyContent: "center",
  marginTop: "24px",
})

const PoweredByText = styled.p({
  color: "#3B4258",
  fontSize: "0.675rem",
  margin: "16px 8px 0 0",
})

const PoweredByLogo = styled.img({
  width: 40,
  height: 30,
})
